import { API } from "@/lib/api";

// Single shared <audio> element, unlocked on first user gesture (required by iOS / mobile autoplay rules).
let player = null;
let unlocked = false;

const silentWav = () => {
  const n = 800, buf = new ArrayBuffer(44 + n * 2), v = new DataView(buf);
  const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  w(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); w(8, "WAVE"); w(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, 16000, true); v.setUint32(28, 32000, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, "data"); v.setUint32(40, n * 2, true);
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
};

export const getPlayer = () => {
  if (!player) {
    player = new Audio();
    player.setAttribute("playsinline", "");
    player.preload = "auto";
  }
  return player;
};

export const unlockAudio = () => {
  if (unlocked) return;
  const p = getPlayer();
  if (p.src && !p.paused) { unlocked = true; return; }
  p.src = silentWav();
  p.play().then(() => { unlocked = true; }).catch(() => {});
  try {
    if (window.speechSynthesis) { const u = new SpeechSynthesisUtterance(" "); u.volume = 0; window.speechSynthesis.speak(u); }
  } catch (e) {}
};

export const installAudioUnlock = () => {
  if (typeof window === "undefined" || window.__omyAudioUnlock) return;
  window.__omyAudioUnlock = true;
  const h = () => { unlockAudio(); if (unlocked) ["pointerdown", "touchend", "click", "keydown"].forEach((e) => window.removeEventListener(e, h, true)); };
  ["pointerdown", "touchend", "click", "keydown"].forEach((e) => window.addEventListener(e, h, true));
};

export const ttsUrl = (text, voice) =>
  `${API}/tts?text=${encodeURIComponent(text)}${voice ? `&voice=${encodeURIComponent(voice)}` : ""}&v=2`;

let current = null;

export const stopAudio = () => {
  if (current) { current.cancelled = true; current.resolve(); current = null; }
  if (player) { try { player.pause(); } catch (e) {} }
  if (window.speechSynthesis) window.speechSynthesis.cancel();
};

// Plays a URL on the shared element; resolves on end (or stop), rejects on error / autoplay block.
export const playUrl = (url) => {
  const p = getPlayer();
  if (current) { current.cancelled = true; current.resolve(); }
  return new Promise((resolve, reject) => {
    const job = { resolve, cancelled: false };
    current = job;
    // Only detach handlers if this job is still the active one (a stale AbortError must not clobber the next job).
    const done = (fn) => (e) => { if (current === job) { p.onended = null; p.onerror = null; current = null; } fn(e); };
    p.onended = done(resolve);
    p.onerror = done(() => reject(new Error("audio")));
    p.src = url;
    p.play().catch(done(reject));
  });
};

export const speakFallback = (text, { onstart, onend } = {}) => {
  if (!window.speechSynthesis) { onend && onend(); return; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "fr-FR"; u.rate = 1.03;
  const fr = window.speechSynthesis.getVoices().find((v) => v.lang && v.lang.toLowerCase().startsWith("fr"));
  if (fr) u.voice = fr;
  u.onstart = () => onstart && onstart();
  u.onend = () => onend && onend();
  u.onerror = () => onend && onend();
  window.speechSynthesis.speak(u);
};

// Speech recognition errors that mean "stop retrying".
export const FATAL_SR_ERRORS = ["not-allowed", "service-not-allowed", "audio-capture"];
